// https://media.giphy.com/media/l41lOzTIL8lfZ15fy/giphy.gif
import React, { FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Icon from '@rover/components/src/typography/Icon';

// Accessibility features based on https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html

// NOTE(Ryan) animation of max height is what allows us to animate the Accordion
// for arbitrarily sized body content.  This number should be a little
// larger than the largest possible Accordion body
const INITIAL_IS_OPEN_MAX_HEIGHT = 2880;
const ANIMATION_TIME_MS = 250;

export const AccordionWrapper = styled.dl`
  margin: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};

  &:first-of-type {
    border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  }
`;

export const AccordionHeader = styled.dt``;

export const AccordionHeaderClickTarget = styled.button<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: transparent;
  border: 0;
  padding-top: ${({ theme }) => theme.space['4x']};
  padding-bottom: ${({ theme }) => theme.space['4x']};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  text-align: left;
`;

export const AccordionHeaderContent = styled.div`
  flex: 1 1;
`;

export const AccordionHeaderIconWrapper = styled.div<{ disabled?: boolean }>`
  flex: 0 0;
  padding-left: ${({ theme }) => theme.space['2x']};
  padding-right: ${({ theme }) => theme.space['4x']};
  color: ${(props) =>
    props.disabled ? props.theme.textColor.disabled : props.theme.textColor.tertiary};
`;

export const AccordionBody = styled.dd<{
  isOpen: boolean;
  maxHeight: number;
  allowOverflow?: boolean;
}>`
  overflow: ${({ allowOverflow, isOpen }) => (allowOverflow && isOpen ? 'visible' : 'hidden')};
  height: auto;
  max-height: ${({ isOpen, maxHeight }) => (isOpen ? maxHeight : 0)}px;
  &:focus-within {
    max-height: ${({ maxHeight }) => maxHeight}px;
  }
  transition: max-height ${ANIMATION_TIME_MS / 1000}s ease-in-out;
  margin: 0;
`;

export const AccordionBodyContent = styled.div`
  padding-top: ${({ theme }) => theme.space['4x']};
  padding-bottom: ${({ theme }) => theme.space['4x']};
`;

export type Props = {
  // required props
  header: React.ReactNode; // the top section, always shown
  body: React.ReactNode; // the bottom section, visibility is toggled

  // optional props
  openIcon?: React.ReactNode; // icon for the open state, defaults to down chevron
  closedIcon?: React.ReactNode; // icon for the closed state, defaults to right chevron
  getIcon?: (isOpen: boolean) => React.ReactNode; // boolean arg is the isOpen value of the Accordion, return the icon to use, or undefined to use the defaults
  isStackedAccordion?: boolean; // when stacking accordions use this to get proper accessible markup
  disabled?: boolean;
  // used to control the Accordion via props
  isOpen?: boolean;
  suppressIcon?: boolean;
  isRightIcon?: boolean;
  onToggle?: (isOpen: boolean) => void; // called onClick of the header portion, boolean arg is the next/requested isOpen state
  suppressDynamicHeight?: boolean;
  allowOverflow?: boolean;
  // Omit the native DOM `onToggle` (React 19 typed `HTMLAttributes.onToggle` as
  // `ToggleEventHandler`, which would intersect with — and break — our own
  // `(isOpen: boolean) => void` toggle callback above).
} & Omit<React.HTMLAttributes<HTMLDivElement | HTMLDListElement>, 'onToggle'>;

/**
 *
 * @deprecated Please use [Accordion]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/react-lib/src/components/lists/Accordion/Accordion.tsx}) and [AccordionItem]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/react-lib/src/components/lists/Accordion/AccordionItem.tsx}) instead.
 *
 * See example usage in [the Home Page Footer]({@link https://github.com/roverdotcom/web/blob/master/src/frontend/react-lib/src/pages/shared/FooterLinks/Footer.tsx})
 */
const Accordion: FC<Props> = ({
  openIcon = 'rover-icon-in-down',
  closedIcon = 'rover-icon-in-right',
  getIcon = (): undefined => undefined,
  isStackedAccordion = false,
  suppressIcon = false,
  isRightIcon = false,
  suppressDynamicHeight = false,
  allowOverflow = false,
  isOpen: $isOpen,
  onToggle,
  header,
  body,
  disabled,
  ...other
}) => {
  const buttonId = useId();
  const bodyId = useId();
  const bodyWrapper = useRef<HTMLElement | null>(null);
  const bodyContent = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState($isOpen || false);
  const [maxHeight, setMaxHeight] = useState(INITIAL_IS_OPEN_MAX_HEIGHT);

  useEffect(() => {
    if ($isOpen !== undefined) {
      setIsOpen($isOpen);
    }
  }, [$isOpen]);

  const icon = useMemo(() => {
    const $icon = getIcon(isOpen);
    if ($icon) return $icon;
    return isOpen ? openIcon : closedIcon;
  }, [getIcon, isOpen, openIcon, closedIcon]);

  const updateMaxHeight = useCallback((): void => {
    if (bodyContent.current) {
      const $maxHeight = bodyContent.current.offsetHeight;
      setMaxHeight($maxHeight);
    }
  }, [bodyContent, setMaxHeight]);

  useEffect(() => {
    if (!suppressDynamicHeight)
      bodyWrapper.current?.addEventListener('transitionend', updateMaxHeight);

    return () => {
      bodyWrapper.current?.removeEventListener('transitionend', updateMaxHeight);
    };
  }, [suppressDynamicHeight, bodyWrapper, updateMaxHeight]);

  const handleClick = useCallback((): void => {
    if (disabled) return;
    onToggle?.(!isOpen);
    setIsOpen(!isOpen);
  }, [onToggle, setIsOpen, isOpen, disabled]);

  const iconWrapper = !suppressIcon ? (
    <AccordionHeaderIconWrapper disabled={disabled}>
      {typeof icon === 'string' ? <Icon icon={icon} /> : icon || undefined}
    </AccordionHeaderIconWrapper>
  ) : null;

  return (
    <AccordionWrapper as={isStackedAccordion ? 'div' : undefined} {...other}>
      <AccordionHeader>
        <AccordionHeaderClickTarget
          type="button"
          aria-controls={bodyId}
          aria-expanded={isOpen}
          disabled={disabled}
          aria-disabled={disabled}
          id={buttonId}
          onClick={handleClick}
        >
          {!isRightIcon && iconWrapper}
          <AccordionHeaderContent>{header}</AccordionHeaderContent>
          {isRightIcon && iconWrapper}
        </AccordionHeaderClickTarget>
      </AccordionHeader>
      <AccordionBody
        data-testid="accordion-body"
        aria-labelledby={buttonId}
        id={bodyId}
        isOpen={isOpen}
        maxHeight={maxHeight}
        allowOverflow={allowOverflow}
        ref={bodyWrapper}
      >
        <AccordionBodyContent ref={bodyContent}>{body}</AccordionBodyContent>
      </AccordionBody>
    </AccordionWrapper>
  );
};

export default Accordion;
